import { memo, useMemo } from 'react';
import styledWithTheme from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_INDENT_SIZE = 24;
const DEFAULT_LINE_WEIGHT = 1;
const DEFAULT_LINE_COLOR = 'border-core-subtle';
const IndentWrapper = styledWithTheme.div(({
  depth,
  indentSize = 0
}) => {
  return {
    paddingInlineStart: indentSize * depth,
    position: 'relative'
  };
});
const EndpointSegment = styledWithTheme.div(({
  style,
  lineColor = 'border-core-subtle',
  theme
}) => {
  return Object.assign({
    position: 'absolute',
    background: theme.color[lineColor]
  }, style);
});
const RepeatingLinesContainer = styledWithTheme.div({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'row'
});
const HalfWidthSpacer = styledWithTheme.div(({
  indentSize = DEFAULT_INDENT_SIZE,
  lineWeight = DEFAULT_LINE_WEIGHT
}) => {
  return {
    width: indentSize / 2 - lineWeight,
    // we subtract the line weight to adjust the offset of the rendered lines
    flexShrink: 0
  };
});
const RepeatingLineSegment = styledWithTheme.div(({
  indentSize,
  lineWeight,
  lineColor = DEFAULT_LINE_COLOR,
  theme
}) => {
  return {
    width: indentSize,
    borderLeft: `${lineWeight}px solid ${theme.color[lineColor]}`,
    flexShrink: 0,
    position: 'relative'
  };
});
const NestingStructureItemWrapper = /*#__PURE__*/memo(({
  depth,
  previousDepth,
  indentSize = DEFAULT_INDENT_SIZE,
  lineWeight = DEFAULT_LINE_WEIGHT,
  endpointWidth,
  lineColor = DEFAULT_LINE_COLOR,
  children
}) => {
  const midpointOffset = indentSize / 2;
  const adjustedMidpointOffset = midpointOffset + lineWeight;
  const currentDepthSpacingValue = indentSize * depth - adjustedMidpointOffset;
  const previousDepthSpacingValueInclusive = indentSize * (depth - 1) - midpointOffset;
  const previousDepthSpacingValueExclusive = previousDepthSpacingValueInclusive - lineWeight;
  const calculatedEndpointWidth = useMemo(() => {
    if (endpointWidth) {
      return endpointWidth;
    }

    // Calculated value based on indent size and line weight to ensure proper alignment
    // the 1/3 factor was determined through visual testing to achieve the best (subjective) appearance
    return indentSize / 3 + lineWeight;
  }, [lineWeight, endpointWidth, indentSize]);
  const isSameAsPreviousDepth = depth > 0 && previousDepth === depth;
  return /*#__PURE__*/_jsxs(IndentWrapper, {
    depth: depth,
    indentSize: indentSize,
    children: [depth > 0 && /*#__PURE__*/_jsxs(RepeatingLinesContainer, {
      children: [/*#__PURE__*/_jsx(HalfWidthSpacer, {
        indentSize: indentSize,
        lineWeight: lineWeight
      }), Array.from({
        length: depth - 1
      }).map((_, index) => /*#__PURE__*/_jsx(RepeatingLineSegment, {
        indentSize: indentSize,
        lineWeight: lineWeight,
        lineColor: lineColor
      }, index)), isSameAsPreviousDepth && /*#__PURE__*/_jsx(EndpointSegment, {
        style: {
          left: currentDepthSpacingValue,
          height: '50%',
          top: '-50%',
          width: lineWeight
        },
        lineColor: lineColor
      }), /*#__PURE__*/_jsx(EndpointSegment, {
        style: {
          left: currentDepthSpacingValue,
          height: '50%',
          width: lineWeight
        },
        lineColor: lineColor
      }), /*#__PURE__*/_jsx(EndpointSegment, {
        style: {
          left: currentDepthSpacingValue,
          // centers the horizontal line with the current item vertical line
          // imperceptible with default 1px line weight, but important for heavier line weights
          top: `calc(50% - ${lineWeight}px / 2)`,
          height: lineWeight,
          width: calculatedEndpointWidth
        },
        lineColor: lineColor
      })]
    }), depth > 1 && /* acts as the bottom half of the vertical line when we have this: ⊦ type of shape */
    previousDepth < depth && /*#__PURE__*/_jsx(EndpointSegment, {
      style: {
        left: previousDepthSpacingValueExclusive,
        height: '50%',
        top: '-50%',
        width: lineWeight
      },
      lineColor: lineColor
    }), children]
  });
});
export default NestingStructureItemWrapper;